import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  registerEntity, appendTelemetry, deleteEntity, listEntities, getLatestTelemetry,
  EntityType, EntityRecord,
} from '../services/entitiesService';

/**
 * RegisterTestEntityOverlay
 *
 * Admin utility for exercising the MAVLink/telemetry layer without a real
 * autopilot.  Registers synthetic drone/vehicle/GCS entities near the
 * logged-in user's location and posts NormalizedTelemetry frames on a
 * 2s tick so the TelemetryLayer markers move visibly on the map.
 *
 * Features:
 *   - Minimize to a small floating pill (animations keep running;
 *     the user can browse the map freely)
 *   - Per-entity actions: pause/resume animation, teleport to me,
 *     unregister (DELETE /api/entities/:id)
 *   - Auto-discovers existing drone/vehicle/GCS entities on open so
 *     clean-up after a page refresh is possible without hitting the
 *     REST endpoint by hand
 *
 * The component is mounted unconditionally when `isAdmin` in App.tsx
 * and uses its own `view` state ('open'|'minimized'|'closed') instead
 * of blindly following `isOpen`, so animations survive map navigation.
 */

export interface RegisterTestEntityOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  /** Current user location ([lng, lat]) — used to seed entities near you. */
  playerLocation?: [number, number] | null;
}

type SupportedType = Extract<EntityType, 'drone' | 'vehicle' | 'ground_control_station'>;

const TYPE_OPTIONS: Array<{ id: SupportedType; label: string; icon: string }> = [
  { id: 'drone',                  label: 'Drone',       icon: '✈' },
  { id: 'vehicle',                label: 'Vehicle',     icon: '🚗' },
  { id: 'ground_control_station', label: 'GCS (base)',  icon: '🎮' },
];

function iconFor(t: EntityType): string {
  const opt = TYPE_OPTIONS.find((o) => o.id === t);
  return opt?.icon ?? '❔';
}

function randomName(type: SupportedType): string {
  const tag = Math.random().toString(36).slice(2, 6).toUpperCase();
  const base = type === 'drone' ? 'TEST-DRONE'
             : type === 'vehicle' ? 'TEST-VEH'
             : 'TEST-GCS';
  return `${base}-${tag}`;
}

/** Add (dLat, dLon) metres around an origin lat/lon (small-angle flat-Earth). */
function offsetLatLon(
  originLat: number,
  originLon: number,
  dLatMeters: number,
  dLonMeters: number,
): { lat: number; lon: number } {
  const metersPerDegLat = 111_132;
  const metersPerDegLon = 111_132 * Math.cos((originLat * Math.PI) / 180);
  return {
    lat: originLat + dLatMeters / metersPerDegLat,
    lon: originLon + dLonMeters / Math.max(1, metersPerDegLon),
  };
}

function buildTelemetry(opts: {
  entityId: string;
  entityType: SupportedType;
  lat: number;
  lon: number;
  headingDeg: number;
  groundSpeed: number;
  tick: number;
}) {
  const { entityId, entityType, lat, lon, headingDeg, groundSpeed, tick } = opts;
  return {
    entityId,
    entityType,
    ts: Date.now(),
    source: 'test_overlay',
    trust: 'unverified' as const,
    position: {
      lat, lon,
      alt_msl: entityType === 'drone' ? 60 + (tick % 20) : 2,
      alt_rel: entityType === 'drone' ? 50 + (tick % 20) : 0,
    },
    attitude: {
      roll_deg:  entityType === 'drone' ? Math.sin(tick * 0.1) * 5 : 0,
      pitch_deg: entityType === 'drone' ? Math.cos(tick * 0.1) * 3 : 0,
      yaw_deg:   headingDeg,
    },
    velocity: {
      ground_speed_mps:  groundSpeed,
      heading_deg:       headingDeg,
      vertical_speed_mps: 0,
    },
    battery: entityType === 'ground_control_station' ? null : {
      remaining_pct: Math.max(5, 95 - (tick % 90)),
      voltage_v: 22.1 - (tick % 10) * 0.05,
    },
    gps: { satellites: 14, fix_type: 3 },
    flight_mode: entityType === 'drone' ? 'AUTO'
               : entityType === 'vehicle' ? 'DRIVE'
               : 'IDLE',
  };
}

interface Row {
  entity: EntityRecord;
  lat: number | null;
  lon: number | null;
  heading: number;
  speed: number;
  tick: number;
  /** null when not animating */
  timer: number | null;
  /** tracks whether this row was registered during the current session */
  sessionCreated: boolean;
}

type View = 'closed' | 'open' | 'minimized';

export const RegisterTestEntityOverlay: React.FC<RegisterTestEntityOverlayProps> = ({
  isOpen,
  onClose,
  playerLocation,
}) => {
  const [view, setView] = useState<View>('closed');
  const [rows, setRows] = useState<Row[]>([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const [entityType, setEntityType] = useState<SupportedType>('drone');
  const [displayName, setDisplayName] = useState<string>('');
  const [animate, setAnimate] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  // ── View management ──
  // Respond to parent "please open" signals without losing our minimized
  // state when other overlays steal activeOverlay from us.
  useEffect(() => {
    if (isOpen) {
      setView('open');
      setError(null);
      setMessage(null);
    }
  }, [isOpen]);

  // Auto-refresh the list whenever we open.  This picks up entities
  // created in prior sessions so admins can clean them up.
  const refreshList = useCallback(async () => {
    try {
      const lists = await Promise.all([
        listEntities({ type: 'drone' }).catch(() => []),
        listEntities({ type: 'vehicle' }).catch(() => []),
        listEntities({ type: 'ground_control_station' }).catch(() => []),
      ]);
      const all = lists.flat();
      // Merge: keep rows we already have (preserves timers), append new ones.
      const existingIds = new Set(rowsRef.current.map((r) => r.entity.id));
      const additions: Row[] = [];
      for (const e of all) {
        if (existingIds.has(e.id)) continue;
        let lat: number | null = null;
        let lon: number | null = null;
        try {
          const tel = await getLatestTelemetry(e.id);
          if (tel?.position) { lat = tel.position.lat; lon = tel.position.lon; }
        } catch { /* ignore */ }
        additions.push({
          entity: e,
          lat, lon,
          heading: Math.floor(Math.random() * 360),
          speed: e.entityType === 'drone' ? 8 : e.entityType === 'vehicle' ? 12 : 0,
          tick: 0,
          timer: null,
          sessionCreated: false,
        });
      }
      const livingIds = new Set(all.map((e) => e.id));
      setRows((prev) => {
        const kept = prev.filter((r) => livingIds.has(r.entity.id) || r.sessionCreated);
        return [...kept, ...additions];
      });
    } catch { /* best effort */ }
  }, []);

  useEffect(() => {
    if (view === 'open') { void refreshList(); }
  }, [view, refreshList]);

  // Cleanup: stop every animation timer when the component unmounts.
  useEffect(() => () => {
    rowsRef.current.forEach((r) => { if (r.timer) window.clearInterval(r.timer); });
  }, []);

  // ── Seed defaults ──
  useEffect(() => {
    if (!displayName) setDisplayName(randomName(entityType));
  }, [entityType]); // eslint-disable-line react-hooks/exhaustive-deps

  const origin = useMemo<{ lat: number; lon: number } | null>(() => {
    if (!playerLocation) return null;
    const [lon, lat] = playerLocation;
    const off = offsetLatLon(lat, lon, 200, 200);
    return { lat: off.lat, lon: off.lon };
  }, [playerLocation]);

  // ── Animation ──
  const startAnimator = useCallback((entityId: string) => {
    const existing = rowsRef.current.find((r) => r.entity.id === entityId);
    if (!existing || existing.timer) return;

    const timer = window.setInterval(() => {
      const current = rowsRef.current.find((r) => r.entity.id === entityId);
      if (!current || current.lat == null || current.lon == null) return;

      const nextTick = current.tick + 1;
      const dt = 2;
      const dist = current.speed * dt;
      const rad = (current.heading * Math.PI) / 180;
      const dNorth = Math.cos(rad) * dist;
      const dEast  = Math.sin(rad) * dist;
      const next = offsetLatLon(current.lat, current.lon, dNorth, dEast);

      const nextHeading = nextTick % 15 === 0
        ? (current.heading + 90 + Math.floor(Math.random() * 40)) % 360
        : current.heading;

      // Persist frame to the server — this is what the TelemetryLayer
      // picks up via /ws/mavlink (if wired) or the REST poll.
      appendTelemetry(entityId, buildTelemetry({
        entityId,
        entityType: current.entity.entityType as SupportedType,
        lat: next.lat, lon: next.lon,
        headingDeg: nextHeading,
        groundSpeed: current.speed,
        tick: nextTick,
      })).catch(() => { /* best effort */ });

      setRows((prev) => prev.map((r) => r.entity.id === entityId
        ? { ...r, lat: next.lat, lon: next.lon, heading: nextHeading, tick: nextTick }
        : r));
    }, 2000);

    setRows((prev) => prev.map((r) => r.entity.id === entityId ? { ...r, timer } : r));
  }, []);

  const stopAnimator = useCallback((entityId: string) => {
    setRows((prev) => prev.map((r) => {
      if (r.entity.id !== entityId) return r;
      if (r.timer) window.clearInterval(r.timer);
      return { ...r, timer: null };
    }));
  }, []);

  // ── Register a new entity ──
  async function handleRegister() {
    setError(null);
    setMessage(null);
    if (!origin) {
      setError('no_player_location — open the map, let location resolve, then retry.');
      return;
    }
    setBusy(true);
    try {
      const entity = await registerEntity({
        entityType,
        displayName: displayName.trim() || randomName(entityType),
        systemId: entityType === 'drone' ? 1 : 2,
        componentId: 1,
        autopilotType: entityType === 'drone' ? 12 : 0,
        vehicleType:   entityType === 'drone' ? 2 : 10,
        firmwareType: 0,
        metadataHash: null,
      } as any);

      const heading = Math.floor(Math.random() * 360);
      const speed   = entityType === 'drone' ? 8 : entityType === 'vehicle' ? 12 : 0;

      await appendTelemetry(entity.id, buildTelemetry({
        entityId: entity.id,
        entityType,
        lat: origin.lat,
        lon: origin.lon,
        headingDeg: heading,
        groundSpeed: speed,
        tick: 0,
      }));

      const row: Row = {
        entity,
        lat: origin.lat,
        lon: origin.lon,
        heading, speed,
        tick: 0,
        timer: null,
        sessionCreated: true,
      };
      setRows((prev) => [...prev, row]);
      setMessage(`Registered ${iconFor(entity.entityType)} ${entity.displayName}`);
      setDisplayName(randomName(entityType));

      if (animate && entity.entityType !== 'ground_control_station') {
        // Defer so rowsRef picks up the new row before the animator reads it.
        setTimeout(() => startAnimator(entity.id), 0);
      }
    } catch (err: any) {
      setError(err?.message || 'register_failed');
    } finally {
      setBusy(false);
    }
  }

  // ── Unregister / teleport actions ──
  async function handleUnregister(id: string) {
    stopAnimator(id);
    try {
      await deleteEntity(id);
      setRows((prev) => prev.filter((r) => r.entity.id !== id));
      setMessage('Unregistered.');
    } catch (err: any) {
      setError(err?.message || 'delete_failed');
    }
  }

  async function handleTeleport(id: string) {
    if (!origin) { setError('no_player_location'); return; }
    const row = rowsRef.current.find((r) => r.entity.id === id);
    if (!row) return;
    try {
      await appendTelemetry(id, buildTelemetry({
        entityId: id,
        entityType: row.entity.entityType as SupportedType,
        lat: origin.lat, lon: origin.lon,
        headingDeg: row.heading,
        groundSpeed: row.speed,
        tick: row.tick + 1,
      }));
      setRows((prev) => prev.map((r) => r.entity.id === id
        ? { ...r, lat: origin.lat, lon: origin.lon, tick: r.tick + 1 }
        : r));
      setMessage('Teleported to your location.');
    } catch (err: any) {
      setError(err?.message || 'teleport_failed');
    }
  }

  async function handleClearAll() {
    const victims = rowsRef.current.slice();
    victims.forEach((r) => { if (r.timer) window.clearInterval(r.timer); });
    setRows([]);
    setConfirmClearAll(false);
    setMessage('Deleting…');
    let failed = 0;
    for (const r of victims) {
      try { await deleteEntity(r.entity.id); } catch { failed++; }
    }
    setMessage(failed === 0
      ? `Deleted ${victims.length} entit${victims.length === 1 ? 'y' : 'ies'}.`
      : `Deleted ${victims.length - failed}/${victims.length} — ${failed} failed.`);
  }

  function handleMinimize() {
    setView('minimized');
    // Parent's activeOverlay pointer can release; we stay mounted and keep
    // animating because App.tsx mounts us unconditionally for admins.
    onClose();
  }

  function handleClose() {
    // Stop all animations but leave entities on the server; the list can
    // be re-opened later to clean them up.  If the user wants to wipe
    // everything, they can use "Delete all" first.
    rowsRef.current.forEach((r) => { if (r.timer) window.clearInterval(r.timer); });
    setRows((prev) => prev.map((r) => ({ ...r, timer: null })));
    setView('closed');
    onClose();
  }

  // ── Render ──
  if (view === 'closed') return null;

  const animatingCount = rows.filter((r) => r.timer).length;
  const totalCount     = rows.length;

  if (view === 'minimized') {
    return (
      <div style={pillWrapStyle}>
        <button
          type="button"
          onClick={() => setView('open')}
          title="Open test-entity overlay"
          style={pillButtonStyle}
        >
          <span style={{ fontSize: 16 }}>🧪</span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
              Test entities
            </span>
            <span style={{ fontSize: 11, color: '#cbd5f5' }}>
              {totalCount} total{animatingCount > 0 ? ` · ${animatingCount} moving` : ''}
            </span>
          </div>
          <span style={pillExpandHintStyle} aria-hidden>↗</span>
        </button>
        <button
          type="button"
          onClick={handleClose}
          style={pillStopBtnStyle}
          title="Stop animations and close"
        >
          Close
        </button>
      </div>
    );
  }

  // view === 'open'
  return (
    <div style={backdropStyle} onClick={handleMinimize}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerRowStyle}>
          <h3 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>Register test entity</h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={handleMinimize}
              title="Minimize (animations keep running)"
              aria-label="Minimize"
              style={headerIconBtnStyle}
            >—</button>
            <button
              onClick={handleClose}
              title="Close (stops animations, leaves entities)"
              aria-label="Close"
              style={headerIconBtnStyle}
            >×</button>
          </div>
        </div>

        <p style={descriptionStyle}>
          Creates a synthetic drone, vehicle, or GCS entity near your location
          and posts NormalizedTelemetry frames.  Minimize to keep animations
          running while you browse the map — registered entities show up as
          markers handled by TelemetryLayer.
        </p>

        {/* ── Registration form ── */}
        <div style={formCardStyle}>
          <div style={sectionTitleStyle}>New entity</div>

          <label style={labelStyle}>Type</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setEntityType(opt.id)}
                disabled={busy}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  borderRadius: 8,
                  border: entityType === opt.id ? '2px solid #2563eb' : '1px solid #cbd5e1',
                  background: entityType === opt.id ? '#eff6ff' : '#ffffff',
                  color: '#0f172a',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: entityType === opt.id ? 700 : 500,
                }}
              >
                <span style={{ marginRight: 6 }}>{opt.icon}</span>{opt.label}
              </button>
            ))}
          </div>

          <label style={labelStyle}>Display name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={busy}
            placeholder={randomName(entityType)}
            style={inputStyle}
          />

          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={animate}
              onChange={(e) => setAnimate(e.target.checked)}
              disabled={busy}
            />
            Animate on register (posts a telemetry frame every 2 s)
          </label>

          <div style={originNoteStyle}>
            {origin
              ? <>Seed: <code style={codeStyle}>{origin.lat.toFixed(5)}, {origin.lon.toFixed(5)}</code> (≈200 m from you)</>
              : <span style={{ color: '#b45309' }}>Waiting for your map location…</span>}
          </div>

          <button
            onClick={handleRegister}
            disabled={busy || !origin}
            style={{
              ...primaryBtnStyle,
              background: busy || !origin ? '#94a3b8' : '#2563eb',
              cursor: busy || !origin ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Registering…' : 'Register'}
          </button>
        </div>

        {/* ── Status ── */}
        {error   && <div style={errorBoxStyle}>{error}</div>}
        {message && <div style={successBoxStyle}>{message}</div>}

        {/* ── Registered list ── */}
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
            Registered ({totalCount}{animatingCount > 0 ? ` · ${animatingCount} moving` : ''})
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => void refreshList()} style={secondaryBtnStyle} title="Refresh list">Refresh</button>
            {totalCount > 0 && (
              confirmClearAll ? (
                <>
                  <button onClick={() => setConfirmClearAll(false)} style={secondaryBtnStyle}>Cancel</button>
                  <button onClick={handleClearAll} style={dangerBtnStyle} title="Delete every registered entity on the server">Confirm delete all</button>
                </>
              ) : (
                <button onClick={() => setConfirmClearAll(true)} style={dangerBtnStyle}>Delete all</button>
              )
            )}
          </div>
        </div>

        <div style={{ marginTop: 8, maxHeight: 280, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          {totalCount === 0 ? (
            <div style={{ padding: 14, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
              No entities registered yet. Use the form above to create one.
            </div>
          ) : (
            rows.map((r) => (
              <div key={r.entity.id} style={rowStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 18 }}>{iconFor(r.entity.entityType)}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.entity.displayName || r.entity.id}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'ui-monospace,Menlo,monospace' }}>
                      {r.entity.id.slice(0, 12)}…
                      {r.lat != null && r.lon != null && (
                        <> · {r.lat.toFixed(3)}, {r.lon.toFixed(3)}</>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {r.entity.entityType !== 'ground_control_station' && (
                    r.timer
                      ? <button onClick={() => stopAnimator(r.entity.id)}  style={smallBtnStyle}    title="Pause animation">⏸</button>
                      : <button onClick={() => startAnimator(r.entity.id)} style={smallBtnStyle}    title="Resume animation" disabled={r.lat == null}>▶</button>
                  )}
                  <button
                    onClick={() => handleTeleport(r.entity.id)}
                    style={smallBtnStyle}
                    title="Teleport to my location"
                    disabled={!origin}
                  >📍</button>
                  <button
                    onClick={() => handleUnregister(r.entity.id)}
                    style={smallDangerBtnStyle}
                    title="Unregister (DELETE /api/entities/:id)"
                  >🗑</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterTestEntityOverlay;

// ── Styles ──

const pillWrapStyle: React.CSSProperties = {
  position: 'fixed',
  left: '50%', bottom: 16,
  transform: 'translateX(-50%)',
  display: 'flex', alignItems: 'stretch', gap: 6,
  zIndex: 2900,
  pointerEvents: 'auto',
  maxWidth: 'calc(100vw - 24px)',
};

const pillButtonStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '6px 14px 6px 10px',
  borderRadius: 999,
  background: 'rgba(15, 23, 42, 0.9)',
  color: '#fff',
  border: '1px solid rgba(148,163,184,0.4)',
  cursor: 'pointer',
  boxShadow: '0 10px 30px rgba(15,23,42,0.35)',
  minWidth: 0,
};

const pillStopBtnStyle: React.CSSProperties = {
  padding: '0 14px',
  borderRadius: 999,
  background: '#dc2626',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
};

const pillExpandHintStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#cbd5f5',
  marginLeft: 4,
};

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.55)',
  zIndex: 9500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 12,
  boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
  width: 'min(560px, 100%)',
  maxHeight: '90vh',
  overflow: 'auto',
  padding: 20,
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 10,
};

const headerIconBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  width: 28, height: 28,
  fontSize: 14,
  color: '#475569',
  cursor: 'pointer',
  lineHeight: 1,
};

const descriptionStyle: React.CSSProperties = {
  margin: '0 0 14px 0',
  color: '#475569',
  fontSize: 13,
  lineHeight: 1.5,
};

const formCardStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: 14,
  background: '#f8fafc',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: '#334155',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 8,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 6,
  fontSize: 12,
  color: '#334155',
  fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  marginBottom: 10,
  boxSizing: 'border-box',
};

const originNoteStyle: React.CSSProperties = {
  background: '#eef2ff',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 12,
  color: '#334155',
  marginBottom: 10,
};

const codeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace,Menlo,monospace',
  fontSize: 12,
  background: '#ffffff',
  padding: '1px 4px',
  borderRadius: 4,
  border: '1px solid #e2e8f0',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 6,
  border: 'none',
  color: '#ffffff',
  fontSize: 13,
  fontWeight: 600,
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#ffffff',
  color: '#0f172a',
  cursor: 'pointer',
  fontSize: 12,
};

const dangerBtnStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 6,
  border: 'none',
  background: '#dc2626',
  color: '#ffffff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
};

const errorBoxStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: '#fee2e2',
  color: '#991b1b',
  border: '1px solid #fecaca',
  borderRadius: 6,
  fontSize: 13,
  marginTop: 12,
};

const successBoxStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: '#ecfdf5',
  color: '#065f46',
  border: '1px solid #a7f3d0',
  borderRadius: 6,
  fontSize: 13,
  marginTop: 12,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  borderBottom: '1px solid #f1f5f9',
};

const smallBtnStyle: React.CSSProperties = {
  width: 30, height: 28,
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#ffffff',
  color: '#0f172a',
  cursor: 'pointer',
  fontSize: 13,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const smallDangerBtnStyle: React.CSSProperties = {
  width: 30, height: 28,
  borderRadius: 6,
  border: '1px solid #fecaca',
  background: '#fef2f2',
  color: '#991b1b',
  cursor: 'pointer',
  fontSize: 13,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
